import { siteName } from 'config/site';
import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import LandingAuth from './components/Auth/LandingAuth';
import VerifyEmail from './components/Auth/VerifyEmail';
import ResetPassword from './components/Auth/ResetPassword';
import AuthFlowLayout from './components/Auth/AuthFlowLayout';
import Dashboard from './components/Dashboard/Dashboard';
import Privacy from './components/Legal/Privacy';
import Terms from './components/Legal/Terms';
import { setAuthToken } from './services/api';

// SEO content pages — lazy loaded for performance
const ResumeBuilderAI = lazy(() => import('./pages/content/ResumeBuilderAI'));
const ATSFriendlyResume = lazy(() => import('./pages/content/ATSFriendlyResume'));
const ResumeFormatGuide = lazy(() => import('./pages/content/ResumeFormatGuide'));
const ResumeExamples = lazy(() => import('./pages/content/ResumeExamples'));
const ResumeSummaryExamples = lazy(() => import('./pages/content/ResumeSummaryExamples'));
const AIResumeWriter = lazy(() => import('./pages/content/AIResumeWriter'));
const HowToMakeResume = lazy(() => import('./pages/content/HowToMakeResume'));
const ResumeKeywordsGuide = lazy(() => import('./pages/content/ResumeKeywordsGuide'));
const ResumeTemplate2026 = lazy(() => import('./pages/content/ResumeTemplate2026'));
const ResumeMistakes = lazy(() => import('./pages/content/ResumeMistakes'));

// Authority pages
const About = lazy(() => import('./pages/authority/About'));
const AboutAuthor = lazy(() => import('./pages/authority/AboutAuthor'));
const Contact = lazy(() => import('./pages/authority/Contact'));

// Blog
const BlogIndex = lazy(() => import('./pages/blog/BlogIndex'));
const BlogPost = lazy(() => import('./pages/blog/BlogPost'));

// Guides hub
const Guides = lazy(() => import('./pages/Guides'));

// Billing
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess'));

// Account
const Account = lazy(() => import('./pages/Account'));

const PageLoader = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
    <CircularProgress />
  </Box>
);

const AppContent = () => {
  const { user, loading, idToken } = useAuth();
  const [authMode, setAuthMode] = useState('landing'); // 'landing', 'verify', 'reset'
  const [emailToVerify, setEmailToVerify] = useState('');
  const [emailToReset, setEmailToReset] = useState('');

  React.useEffect(() => {
    if (idToken) {
      setAuthToken(idToken);
    }
  }, [idToken]);

  if (loading) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <CircularProgress 
          size={50}
          thickness={4}
          sx={{ color: 'white' }}
        />
        <Box
          sx={{
            color: 'white',
            fontSize: '1.2rem',
            fontWeight: 600,
            letterSpacing: '0.5px',
            textAlign: 'center'
          }}
        >{siteName}</Box>
      </Box>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Legal pages */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />

        {/* SEO Content Cluster Pages */}
        <Route path="/resume-builder-ai" element={<ResumeBuilderAI />} />
        <Route path="/ats-friendly-resume" element={<ATSFriendlyResume />} />
        <Route path="/resume-format-guide" element={<ResumeFormatGuide />} />
        <Route path="/resume-examples" element={<ResumeExamples />} />
        <Route path="/resume-summary-examples" element={<ResumeSummaryExamples />} />
        <Route path="/ai-resume-writer" element={<AIResumeWriter />} />
        <Route path="/how-to-make-resume" element={<HowToMakeResume />} />
        <Route path="/resume-keywords-guide" element={<ResumeKeywordsGuide />} />
        <Route path="/resume-template-2026" element={<ResumeTemplate2026 />} />
        <Route path="/resume-mistakes" element={<ResumeMistakes />} />

        {/* Authority Pages */}
        <Route path="/about" element={<About />} />
        <Route path="/about-author" element={<AboutAuthor />} />
        <Route path="/contact" element={<Contact />} />

        {/* Guides hub */}
        <Route path="/guides" element={<Guides />} />

        {/* Billing */}
        <Route path="/checkout-success" element={<CheckoutSuccess />} />

        {/* Blog */}
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* Protected and Auth routes */}
        {!user ? (
          <>
            {authMode === 'verify' ? (
              <Route
                path="*"
                element={
                  <VerifyEmail
                    email={emailToVerify}
                    onVerified={() => setAuthMode('landing')}
                  />
                }
              />
            ) : authMode === 'reset' ? (
              <Route
                path="*"
                element={
                  <AuthFlowLayout
                    title="Reset password"
                    subtitle="Enter the code from your email and choose a new password"
                    onBack={() => {
                      setAuthMode('landing');
                      setEmailToReset('');
                    }}
                  >
                    <ResetPassword
                      email={emailToReset}
                      onBackToLogin={() => {
                        setAuthMode('landing');
                        setEmailToReset('');
                      }}
                      onPasswordReset={() => {
                        setAuthMode('landing');
                        setEmailToReset('');
                      }}
                    />
                  </AuthFlowLayout>
                }
              />
            ) : (
              <Route
                path="*"
                element={
                  <LandingAuth
                    onVerificationNeeded={(email) => {
                      setEmailToVerify(email);
                      setAuthMode('verify');
                    }}
                    onCodeSent={(email) => {
                      setEmailToReset(email);
                      setAuthMode('reset');
                    }}
                  />
                }
              />
            )}
          </>
        ) : (
          <>
            <Route path="/" element={<Dashboard />} />
            <Route path="/account" element={<Account />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        )}
      </Routes>
    </Suspense>
  );
};

const App = () => {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Router>
          <AppContent />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
};

export default App;

